import React, { useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { Datagrid, TextField } from 'react-admin'

import './OfferBoardTabs.scss'
import { makePrice } from '../../../utils'
import offerBoardService from '../../../models/offerBoard/offerBoardService'
import OfferBoardSocket from '../../../utils/offerBoard/offerBoardSocket'

const LOTS = 'lots'
const LISTED_LOTS = 'listedLots'
const OFFERS = 'offers'
const SOLD_LOTS = 'soldLots'
const PASSED_IN = 'passedIn'

const PENDING = 'Pending'
// a listing that is on the board right now, open for offers
const LISTED = 'Listed'

// an offer still waiting on somebody can be answered from here
const OPEN_OFFER_STATUSES = ['pending', 'countered']

// The whole back and forth of an offer, oldest first: the Buyer's offer, then
// every counter either side answered with. An offer countered before both sides
// could counter kept no history, only the one counter the Seller made.
const offerHistory = (offer) => {
  let counters = Array.isArray(offer.counters) ? offer.counters : []
  if (!counters.length && offer.counterCents) {
    counters = [{ cents: offer.counterCents, party: offer.counterParty, at: offer.respondedAt }]
  }
  return [{ counter: false, cents: offer.cents, party: 'buyer', at: offer.createdAt }].concat(
    counters.map(one => ({
      counter: true,
      cents: one.cents,
      party: one.party || 'seller',
      at: one.at
    }))
  )
}

// Mirrors the server: an offer is waiting on whoever did not put the amount on
// the table - the Seller until he counters, then the Buyer, and so on for as
// many rounds as the two of them want. An admin answers for either side.
const awaitingParty = (offer) => {
  if (offer.status !== 'countered') {
    return 'seller'
  }
  // a counter from before the Buyer could counter back carries no side - it was
  // the Seller's
  return offer.counterParty === 'buyer' ? 'seller' : 'buyer'
}

// how an offer reads in the admin
const OFFER_STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  countered: 'Countered',
  withdrawn: 'Withdrawn'
}

// What the tab in view can be filtered by. The listings and the offers hold
// different things, so each gets its own set.
const LOT_FILTERS = [
  { id: 'lotId', name: 'Lot ID' },
  { id: 'lotNumber', name: 'Lot No' },
  { id: 'description', name: 'Lot Description' },
  { id: 'kind', name: 'Category' },
  { id: 'seller', name: 'Seller' },
  { id: 'status', name: 'Status', options: ['Draft', 'Pending', 'Listed', 'Sold', 'Expired', 'Withdrawn', 'Removed', 'Closed'] },
  { id: 'listedAt', name: 'Listed', type: 'date' },
  { id: 'expiresAt', name: 'Expires', type: 'date' }
]

const OFFER_FILTERS = [
  { id: 'lotNumber', name: 'Lot No' },
  { id: 'description', name: 'Lot Description' },
  { id: 'buyer', name: 'Buyer' },
  { id: 'seller', name: 'Seller' },
  { id: 'status', name: 'Status', options: ['Pending', 'Accepted', 'Declined', 'Countered', 'Withdrawn'] },
  { id: 'madeAt', name: 'Offer Made', type: 'date' }
]

const lotStatus = (lot) => {
  if (lot.removed) {
    return 'Removed'
  }
  if (lot.withdrawn) {
    return 'Withdrawn'
  }
  if (lot.draft) {
    return 'Draft'
  }
  if (!lot.approved) {
    return PENDING
  }
  if (lot.status === 'sold' || lot.status === 'soldAfterAuction') {
    return 'Sold'
  }
  if (lot.state === 'closed') {
    return 'Closed'
  }
  if (lot.expiresAt && new Date(lot.expiresAt) < new Date()) {
    return 'Expired'
  }
  return 'Listed'
}

const formatDate = (value) => value ? moment(value).format('DD/MM/YYYY') : ''

const toRow = (lot) => {
  const details = lot.publicDetails || {}
  const creator = lot.creatorData || {}
  const seller = creator.tradingName ||
    `${creator.firstName || ''} ${creator.lastName || ''}`.trim()
  return {
    id: lot._id,
    kindId: lot.kind,
    sellerId: lot.createdBy,
    lotId: lot.searchNumericId || '',
    // the number the listing has on the board, given when it goes on it - a
    // draft has none yet
    lotNumber: lot.number || '',
    description: details.title || lot.description || '',
    kind: lot.kindData ? lot.kindData.title : '',
    seller,
    headCount: lot.count,
    askingPrice: lot.startPrice ? makePrice(lot.startPrice, { bidding: lot.bidding }) : '',
    listedAt: formatDate(lot.listedAt),
    expiresAt: formatDate(lot.expiresAt),
    daysLeft: typeof lot.daysLeft === 'number' ? lot.daysLeft : '',
    status: lotStatus(lot)
  }
}

// clicking a listing opens the Lot, the way the auction lots list does
const LotLink = ({ record = {}, children }) => (
  <a href={`/#/lots/${record.id}/show`} className='self-link'>{children}</a>
)
const LotIdField = ({ record = {} }) => <LotLink record={record}>{record.lotId}</LotLink>
const LotNumberField = ({ record = {} }) => <LotLink record={record}>{record.lotNumber}</LotLink>
const LotDescriptionField = ({ record = {} }) => <LotLink record={record}>{record.description}</LotLink>
const CategoryField = ({ record = {} }) => (
  record.kindId
    ? <a href={`/#/kinds/${record.kindId}/show`} className='vendor-link'>{record.kind}</a>
    : <span>{record.kind}</span>
)
const SellerField = ({ record = {} }) => (
  record.sellerId
    ? <a href={`/#/users/${record.sellerId}/show`} className='vendor-link'>{record.seller}</a>
    : <span>{record.seller}</span>
)

const LotStatusField = ({ record = {} }) => {
  const highlight = classNames('status-field', {
    sold: record.status === 'Sold',
    listed: record.status === 'Listed',
    passed: record.status === 'Withdrawn' || record.status === 'Removed' ||
      record.status === 'Expired' || record.status === 'Closed',
    noBid: record.status === 'Draft' || record.status === PENDING
  })
  return (
    <span className={highlight}>{record.status}</span>
  )
}

const biddingByLot = (lots = []) => {
  return lots.reduce((all, lot) => {
    return lot && lot._id ? { ...all, [String(lot._id)]: lot.bidding } : all
  }, {})
}

const offerRow = (offer, bidding) => {
  const buyer = offer.creatorData || {}
  const seller = offer.sellerData || {}
  const name = (one) => one.tradingName || `${one.firstName || ''} ${one.lastName || ''}`.trim()
  return {
    id: offer._id,
    lotOwnerId: offer.lot,
    bidding,
    madeAt: offer.createdAt ? moment(offer.createdAt).format('DD/MM/YYYY HH:mm') : '',
    lotNumber: (offer.lotData && (offer.lotData.number || offer.lotData.searchNumericId)) || '',
    description: (offer.lotData && offer.lotData.title) || '',
    buyerId: offer.createdBy,
    buyer: name(buyer),
    sellerId: offer.seller,
    seller: name(seller),
    offer: makePrice(offer.cents, { bidding }),
    // every round of the back and forth, so the whole negotiation is on the row
    // and not just the last counter
    history: offerHistory(offer),
    // how many times the two of them have countered each other so far
    rounds: (offer.counters || []).length || '',
    // whose answer an open offer is waiting on
    withParty: OPEN_OFFER_STATUSES.includes(offer.status)
      ? (awaitingParty(offer) === 'buyer' ? 'Buyer' : 'Seller')
      : '',
    answeredAt: offer.respondedAt ? moment(offer.respondedAt).format('DD/MM/YYYY HH:mm') : '',
    rawStatus: offer.status,
    status: OFFER_STATUS_LABELS[offer.status] || offer.status
  }
}

const OfferLotField = ({ record = {} }) => (
  <a href={`/#/lots/${record.lotOwnerId}/show`} className='self-link'>{record.lotNumber}</a>
)
const OfferDescriptionField = ({ record = {} }) => (
  <a href={`/#/lots/${record.lotOwnerId}/show`} className='self-link'>{record.description}</a>
)
const BuyerField = ({ record = {} }) => (
  record.buyerId
    ? <a href={`/#/users/${record.buyerId}/show`} className='vendor-link'>{record.buyer}</a>
    : <span>{record.buyer}</span>
)

// Every round of an offer on the one row: the offer, then each counter with the
// side that made it. The last one is the amount on the table.
const OfferHistoryField = ({ record = {} }) => {
  const history = record.history || []
  if (history.length < 2) {
    return <span className='offer-history empty'>-</span>
  }
  return (
    <span className='offer-history'>
      {history.slice(1).map((step, index) => (
        <span
          className={classNames('offer-history-step', { latest: index === history.length - 2 })}
          key={index}>
          {makePrice(step.cents, { bidding: record.bidding })} <em>{step.party}</em>
        </span>
      ))}
    </span>
  )
}

const OfferStatusField = ({ record = {} }) => {
  const highlight = classNames('status-field', {
    sold: record.status === 'Accepted',
    passed: record.status === 'Declined' || record.status === 'Withdrawn',
    noBid: record.status === 'Pending' || record.status === 'Countered'
  })
  return (
    <span className={highlight}>{record.status}</span>
  )
}

// Kept outside OfferBoardTabs: declared inside it, every keystroke made a new
// component and remounted the counter input, dropping its focus.
const OfferActionsField = (props) => {
  let { record = {}, readOnly, counterFor, counterInput, setCounterFor, setCounterInput, answer, sendCounter } = props
  if (readOnly || !OPEN_OFFER_STATUSES.includes(record.rawStatus)) {
    return null
  }
  if (counterFor === record.id) {
    return (
      <span className='offer-actions'>
        <input
          className='counter-input'
          type='text'
          placeholder={record.bidding === 'kg' ? '¢ Amount' : '$ Amount'}
          value={counterInput}
          onChange={(e) => {
            if (/^\d*\.?\d*$/.test(e.target.value)) {
              setCounterInput(e.target.value)
            }
          }} />
        <span className='accept' onClick={() => sendCounter(record.id, record.bidding)}>SEND</span>
        <span className='reject' onClick={() => setCounterFor(null)}>CANCEL</span>
      </span>
    )
  }
  return (
    <span className='offer-actions'>
      <span className='accept' onClick={() => answer(record.id, 'accept')}>ACCEPT</span>
      <span className='reject' onClick={() => answer(record.id, 'decline')}>DECLINE</span>
      <span className='counter' onClick={() => setCounterFor(record.id)}>COUNTER</span>
    </span>
  )
}

const InfoTable = (props) => {
  let { rows, children } = props
  const data = rows.reduce((acc, row) => ({ ...acc, [row.id]: row }), {})
  return (
    <div className='info-table-div-wrapper'>
      <Datagrid ids={rows.map(row => row.id)} data={data} currentSort={{}}>
        {children}
      </Datagrid>
    </div>
  )
}

const EmptyTab = ({ message }) => (
  <p className='offer-board-tabs-empty'>{message}</p>
)

// `readOnly` is an archived board being read back: it is finished with, so its
// offers are shown as they were left and are not answered from here.
export const OfferBoardTabs = (props) => {
  let { offerBoard, readOnly = false } = props
  let [tab, setTab] = useState(LOTS)
  let [lots, setLots] = useState([])
  let [offers, setOffers] = useState([])
  let [soldLots, setSoldLots] = useState([])
  let [expiredLots, setExpiredLots] = useState([])
  let [counterFor, setCounterFor] = useState(null)
  let [counterInput, setCounterInput] = useState('')
  let [filterField, setFilterField] = useState('')
  let [filterValue, setFilterValue] = useState('')

  const offerBoardId = offerBoard && (offerBoard.id || offerBoard._id)

  const loadLots = useCallback(async () => {
    if (!offerBoardId) {
      return
    }
    try {
      // fromAdmin, so the drafts and the listings waiting for approval are
      // listed here as well
      const [boardLots, boardOffers, sold, expired] = await Promise.all([
        offerBoardService.getOfferBoardLots(offerBoardId),
        offerBoardService.getOfferBoardOffers(offerBoardId),
        offerBoardService.getOfferBoardLots(offerBoardId, { listing: 'sold' }),
        offerBoardService.getOfferBoardLots(offerBoardId, { listing: 'expired' })
      ])
      const bidding = biddingByLot([...boardLots, ...sold, ...expired])
      setLots(boardLots.map(toRow))
      setOffers(boardOffers.map(offer => offerRow(offer, bidding[String(offer.lot)])))
      setSoldLots(sold.map(toRow))
      setExpiredLots(expired.map(toRow))
    } catch (err) {
      setLots([])
      setOffers([])
      setSoldLots([])
      setExpiredLots([])
    }
  }, [offerBoardId])

  useEffect(() => {
    loadLots()
  }, [loadLots])

  useEffect(() => {
    if (!offerBoardId) {
      return undefined
    }
    const socket = new OfferBoardSocket(offerBoardId, () => loadLots())
    socket.connect()
    return () => socket.disconnect()
  }, [offerBoardId, loadLots])

  // answering an offer from the admin: the Seller can do the same from the
  // Offer Board screen, this is the admin doing it on his behalf
  const answer = async (offerId, action, cents) => {
    try {
      await offerBoardService.answerOffer(offerId, action, cents)
      setCounterFor(null)
      setCounterInput('')
      loadLots()
    } catch (err) {
      setCounterFor(null)
    }
  }

  const sendCounter = (offerId, bidding) => {
    const amount = parseFloat(counterInput)
    if (!amount) {
      return
    }
    answer(offerId, 'counter', bidding === 'kg' ? amount : Math.round(amount * 100))
  }

  const filters = tab === OFFERS ? OFFER_FILTERS : LOT_FILTERS
  const activeFilter = filters.find(one => one.id === filterField)

  const changeFilterField = (field) => {
    setFilterField(field)
    setFilterValue('')
  }

  const changeTab = (id) => {
    setTab(id)
    setFilterField('')
    setFilterValue('')
  }

  const applyFilter = (rows) => {
    if (!activeFilter || !filterValue) {
      return rows
    }
    const needle = activeFilter.type === 'date'
      ? moment(filterValue).format('DD/MM/YYYY')
      : `${filterValue}`.toLowerCase()
    return rows.filter(row => {
      const value = `${row[activeFilter.id] === 0 ? 0 : row[activeFilter.id] || ''}`
      return activeFilter.type === 'date'
        ? value.startsWith(needle)
        : value.toLowerCase().includes(needle)
    })
  }

  const renderFilter = () => (
    <div className='offer-board-tabs-filter'>
      <select
        className='filter-field'
        value={filterField}
        onChange={(e) => changeFilterField(e.target.value)}>
        <option value=''>Filter by</option>
        {filters.map((one) => (
          <option key={one.id} value={one.id}>{one.name}</option>
        ))}
      </select>
      {activeFilter && (activeFilter.options
        ? (
          <select
            className='filter-value'
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}>
            <option value=''>All</option>
            {activeFilter.options.map((one) => (
              <option key={one} value={one}>{one}</option>
            ))}
          </select>
        )
        : (
          <input
            className='filter-value'
            type={activeFilter.type === 'date' ? 'date' : 'text'}
            placeholder={`Search ${activeFilter.name}`}
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)} />
        ))}
      {(filterField || filterValue) &&
        <button type='button' className='filter-clear' onClick={() => changeFilterField('')}>Clear</button>}
    </div>
  )

  const listedLots = lots.filter(lot => lot.status === LISTED)

  const tabs = [
    { id: LOTS, name: 'LOTS', count: lots.length },
    { id: LISTED_LOTS, name: 'LISTED', count: listedLots.length },
    { id: OFFERS, name: 'OFFERS', count: offers.length },
    { id: SOLD_LOTS, name: 'SOLD LOTS', count: soldLots.length },
    { id: PASSED_IN, name: 'PASSED IN', count: expiredLots.length }
  ]

  const renderLots = (rows) => (
    <InfoTable rows={rows}>
      <LotIdField label='Lot ID' />
      <LotNumberField label='Lot No' />
      <LotDescriptionField label='Lot Description' />
      <CategoryField label='Category' />
      <SellerField label='Seller' />
      <TextField source='headCount' label='Head Count' sortable={false} />
      <TextField source='askingPrice' label='Sell Price' sortable={false} />
      <TextField source='listedAt' label='Listed' sortable={false} />
      <TextField source='expiresAt' label='Expires' sortable={false} />
      <TextField source='daysLeft' label='Days Left' sortable={false} />
      <LotStatusField label='Status' />
    </InfoTable>
  )

  const renderTab = () => {
    switch (tab) {
      case LISTED_LOTS:
        if (!listedLots.length) {
          return <EmptyTab message='No lots are listed on this Offer Board.' />
        }
        return renderLots(applyFilter(listedLots))
      case OFFERS:
        if (!offers.length) {
          return <EmptyTab message='No offers have been made on this Offer Board.' />
        }
        return (
          <InfoTable rows={applyFilter(offers)}>
            <TextField source='madeAt' label='Offer Made' sortable={false} />
            <OfferLotField label='Lot No' />
            <OfferDescriptionField label='Lot Description' />
            <SellerField label='Seller' />
            <BuyerField label='Buyer' />
            <TextField source='offer' label='Offer' sortable={false} />
            <OfferHistoryField label='Countered' />
            <TextField source='rounds' label='Rounds' sortable={false} />
            <TextField source='withParty' label='With' sortable={false} />
            <TextField source='answeredAt' label='Answered' sortable={false} />
            <OfferStatusField label='Status' />
            <OfferActionsField
              label='Action'
              readOnly={readOnly}
              counterFor={counterFor}
              counterInput={counterInput}
              setCounterFor={setCounterFor}
              setCounterInput={setCounterInput}
              answer={answer}
              sendCounter={sendCounter} />
          </InfoTable>
        )
      case SOLD_LOTS:
        if (!soldLots.length) {
          return <EmptyTab message='No lots have been sold from this Offer Board.' />
        }
        return renderLots(applyFilter(soldLots))
      case PASSED_IN:
        if (!expiredLots.length) {
          return <EmptyTab message='No lots have expired on this Offer Board.' />
        }
        return renderLots(applyFilter(expiredLots))
      default:
        if (!lots.length) {
          return <EmptyTab message='No lots have been added to this Offer Board.' />
        }
        return renderLots(applyFilter(lots))
    }
  }

  return (
    <div className='offer-board-tabs'>
      <div className='offer-board-tabs-menu'>
        {tabs.map((one) => (
          <button
            key={one.id}
            type='button'
            className={classNames('offer-board-tab', { active: tab === one.id })}
            onClick={() => changeTab(one.id)}>
            {one.name}<span className='count'>{one.count}</span>
          </button>
        ))}
        {renderFilter()}
      </div>
      <div className='offer-board-tabs-body'>
        {renderTab()}
      </div>
    </div>
  )
}
